import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useMediaQuery } from "@mui/material"
import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore"
import { DateTime } from "luxon"
import { useAuthState } from "react-firebase-hooks/auth"
import {
  RaffleDrawingView,
  type RaffleDrawingTicket,
  type RaffleDrawingUserDetails,
  type RaffleDrawingConfig,
  type RaffleDrawingWinnerEntry,
} from "react-ui-components/components/raffle/RaffleDrawingView"
import { PopupService } from "react-ui-components/components/PopupService"

import { auth, db } from "../firebase"
import { getLocationStr } from "../utils"

// ─── Drawing presence helpers ─────────────────────────────────────────────────

const HEARTBEAT_INTERVAL_MS = 30_000
const VIEWER_STALENESS_MS = 90_000

const startPresenceSession = (
  raffleId: string,
  sessionId: string,
  uid: string,
): (() => void) => {
  const sessionRef = doc(db, `raffles/${raffleId}/drawing-viewers/${sessionId}`)
  setDoc(sessionRef, { uid, lastHeartbeatAt: serverTimestamp(), joinedAt: serverTimestamp() }).catch(
    () => undefined,
  )
  const interval = setInterval(() => {
    updateDoc(sessionRef, { lastHeartbeatAt: serverTimestamp() }).catch(() => undefined)
  }, HEARTBEAT_INTERVAL_MS)
  return () => {
    clearInterval(interval)
    deleteDoc(sessionRef).catch(() => undefined)
  }
}

const watchViewerCount = (
  raffleId: string,
  cb: (count: number) => void,
): (() => void) =>
  onSnapshot(collection(db, `raffles/${raffleId}/drawing-viewers`), (snap) => {
    const cutoff = Date.now() - VIEWER_STALENESS_MS
    let count = 0
    snap.forEach((d) => {
      const ts = d.data()?.lastHeartbeatAt
      const ms = ts?.toMillis ? ts.toMillis() : typeof ts === "number" ? ts : 0
      if (ms >= cutoff) count++
    })
    cb(count)
  })

type RaffleTicketRecord = RaffleDrawingTicket & {
  payerUID?: string
  expirationDate?: number | string | Date | null
  setWinnerDate?: number
  setWinnerUserUID?: string
  // Which prize (0-based, in raffle.prizes order) this ticket won. Only set for raffles with
  // more than one prize — single-prize raffles keep the pre-existing shape. See Trello #167.
  prizeIndex?: number
  prizeId?: string | null
  prizeTitle?: string | null
  [key: string]: unknown
}

type RafflePrize = {
  id?: string
  title: string
  description?: string
  imageUrl?: string
}

type UserRecord = {
  profileImageUrl?: string
  email?: string
  name?: string
  displayName?: string
  phoneNumber?: string
  userAddress?: unknown
  selectedLocation?: unknown
  [key: string]: unknown
}

type RaffleRecord = {
  isLiveMode?: boolean
  ticketDesign?: { showDate?: boolean; [key: string]: unknown }
  prizes?: RafflePrize[]
  [key: string]: unknown
}

type DrawingRuntimeStatus = "idle" | "spinning" | "winner"

type WheelProps = {
  raffleId: string
}

const DRAWING_CONFIG_STORAGE_KEY = "winfacil_drawing_config"

const SPIN_DURATION_MS = 2800
const REDUCED_MOTION_SPIN_MS = 250

const createDrawingActionId = () => `drawing-${Date.now()}-${Math.random().toString(36).slice(2)}`

const normalizeDrawingConfig = (config: RaffleDrawingConfig): RaffleDrawingConfig => {
  const legacyConfig = config as RaffleDrawingConfig & {
    sizes?: RaffleDrawingConfig["sizes"] & { showSegmentNumbers?: boolean }
    animation?: RaffleDrawingConfig["animation"] & { showTicketNumbers?: boolean }
  }
  const legacyShowSegmentNumbers = legacyConfig.sizes?.showSegmentNumbers
  const hasAnimationVisibilityFlag = typeof legacyConfig.animation?.showTicketNumbers === "boolean"

  if (hasAnimationVisibilityFlag || typeof legacyShowSegmentNumbers !== "boolean") {
    return config
  }

  return {
    ...config,
    animation: ({
      ...config.animation,
      showTicketNumbers: legacyShowSegmentNumbers,
    } as unknown) as RaffleDrawingConfig["animation"],
  }
}

const isValidTicket = (ticket: RaffleTicketRecord) => Boolean(ticket.id && ticket.code)

const formatExpirationDate = (value: RaffleTicketRecord["expirationDate"]) => {
  if (!value) return undefined

  if (typeof value === "number") {
    const date = value > 9999999999 ? DateTime.fromMillis(value) : DateTime.fromSeconds(value)
    return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : undefined
  }

  if (typeof value === "string") {
    const date = DateTime.fromISO(value)
    return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : value
  }

  if (value instanceof Date) {
    const date = DateTime.fromJSDate(value)
    return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : undefined
  }

  return undefined
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const toUserDetails = (userData?: UserRecord): RaffleDrawingUserDetails | null => {
  if (!userData) return null

  let location: string | undefined
  try {
    const rawLocation = userData.userAddress || userData.selectedLocation
    location = rawLocation ? getLocationStr(rawLocation as any) : undefined
  } catch {
    location = undefined
  }

  return {
    profileImageUrl: userData.profileImageUrl,
    email: userData.email,
    name: userData.name,
    displayName: userData.displayName,
    phoneNumber: userData.phoneNumber,
    location,
  }
}

const Wheel = ({ raffleId }: WheelProps) => {
  const [drawingConfig, setDrawingConfig] = useState<RaffleDrawingConfig>(() => {
    try {
      const stored = localStorage.getItem(DRAWING_CONFIG_STORAGE_KEY)
      return stored ? normalizeDrawingConfig(JSON.parse(stored) as RaffleDrawingConfig) : {}
    } catch {
      return {}
    }
  })
  const [tickets, setTickets] = useState<RaffleTicketRecord[]>([])
  const [selectedTicket, setSelectedTicket] = useState<RaffleTicketRecord | null>(null)
  const [prizes, setPrizes] = useState<RafflePrize[]>([])
  const [isLoadingTickets, setIsLoadingTickets] = useState(false)
  const [isSpinning, setIsSpinning] = useState(false)
  const [isSavingWinner, setIsSavingWinner] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsTicket, setDetailsTicket] = useState<RaffleTicketRecord | null>(null)
  const [detailsUser, setDetailsUser] = useState<RaffleDrawingUserDetails | null>(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [isLiveMode, setIsLiveMode] = useState(false)
  const [isLiveModeLoaded, setIsLiveModeLoaded] = useState(false)
  // Mirrors the raffle's own ticketDesign.showDate toggle (already respected by the main
  // ticket card) so this admin details dialog stops showing the expiration date
  // unconditionally when the raffle has that toggle turned off. See Trello #182.
  const [showExpirationDate, setShowExpirationDate] = useState(true)
  const [isLiveModeUpdating, setIsLiveModeUpdating] = useState(false)
  const spinTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [user] = useAuthState(auth)
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)")
  const [viewerCount, setViewerCount] = useState<number | undefined>(undefined)
  const sessionIdRef = useRef<string>(`admin-${Math.random().toString(36).slice(2)}`)

  // Load persisted live mode for the raffle.
  useEffect(() => {
    let isMounted = true

    const loadRaffleLiveMode = async () => {
      try {
        const raffleRef = doc(db, "raffles", raffleId)
        const raffleSnap = await getDoc(raffleRef)
        const raffleData = (typeof raffleSnap.data === "function" ? raffleSnap.data() : undefined) as RaffleRecord | undefined
        if (isMounted) {
          setIsLiveMode(Boolean(raffleData?.isLiveMode))
          setShowExpirationDate(raffleData?.ticketDesign?.showDate !== false)
          setPrizes(
            Array.isArray(raffleData?.prizes)
              ? raffleData.prizes.filter((prize): prize is RafflePrize => Boolean(prize && typeof prize.title === "string"))
              : [],
          )
        }
      } catch (error) {
        if (isMounted) {
          PopupService.pushPopup(getErrorMessage(error, "Unable to load live mode status."))
        }
      } finally {
        if (isMounted) {
          setIsLiveModeLoaded(true)
        }
      }
    }

    loadRaffleLiveMode()

    return () => {
      isMounted = false
    }
  }, [raffleId])

  // Mirror drawing runtime status from the live mode toggle.
  useEffect(() => {
    if (!isLiveModeLoaded) return

    const raffleRef = doc(db, "raffles", raffleId)
    const payload = isLiveMode
      ? { isDrawingLive: true, drawingStartedAt: serverTimestamp() }
      : {
          isDrawingLive: false,
          drawingState: {
            status: "idle" as DrawingRuntimeStatus,
            actionId: createDrawingActionId(),
            startedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
        }

    updateDoc(raffleRef, payload).catch(
      () => undefined,
    )
  }, [isLiveMode, isLiveModeLoaded, raffleId])

  // Safety cleanup on unmount.
  useEffect(() => {
    const raffleRef = doc(db, "raffles", raffleId)

    return () => {
      updateDoc(raffleRef, {
        isDrawingLive: false,
        drawingState: {
          status: "idle" as DrawingRuntimeStatus,
          actionId: createDrawingActionId(),
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      }).catch(() => undefined)
    }
  }, [raffleId])

  // Manage presence session + viewer count subscription
  useEffect(() => {
    if (!user) return undefined
    const sessionId = sessionIdRef.current
    const stopPresence = startPresenceSession(raffleId, sessionId, user.uid)
    const unsubCount = watchViewerCount(raffleId, setViewerCount)
    return () => {
      stopPresence()
      unsubCount()
    }
  }, [raffleId, user])

  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const fetchTickets = async () => {
      setIsLoadingTickets(true)
      setSelectedTicket(null)

      try {
        const ticketsCollection = collection(db, `raffles/${raffleId}/tickets`)
        const ticketSnapshot = await getDocs(ticketsCollection)
        const ticketList: RaffleTicketRecord[] = ticketSnapshot.docs.map((ticketDoc) => {
          const data = ticketDoc.data() as Partial<RaffleTicketRecord>
          const record: RaffleTicketRecord = {
            ...data,
            id: String((data as { id?: unknown }).id || ticketDoc.id),
            code: String((data as { code?: unknown }).code || ticketDoc.id),
            expirationDateLabel: formatExpirationDate(data.expirationDate as RaffleTicketRecord["expirationDate"]),
          }
          return record
        })

        if (isMounted) {
          setTickets(ticketList)
        }
      } catch (error) {
        if (isMounted) {
          PopupService.pushPopup(getErrorMessage(error, "Unable to load raffle tickets."))
          setTickets([])
        }
      } finally {
        if (isMounted) {
          setIsLoadingTickets(false)
        }
      }
    }

    if (raffleId) {
      fetchTickets()
    }

    return () => {
      isMounted = false
    }
  }, [raffleId])

  useEffect(() => {
    if (!isFullscreen) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullscreen(false)
      }
    }

    window.addEventListener("keydown", onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [isFullscreen])

  // Winners confirmed so far, ordered by which prize they were drawn for. For single-prize
  // raffles (the common case) this is at most one ticket, preserving prior behavior.
  const confirmedWinners = useMemo(
    () =>
      tickets
        .filter((ticket) => ticket.isWinner)
        .sort((a, b) => (a.prizeIndex ?? 0) - (b.prizeIndex ?? 0)),
    [tickets],
  )

  // Raffles with more than one prize draw one winning ticket per prize, sequentially. See
  // Trello #167 ("Spin Configurable" — let admins draw 3+ winners for multi-prize raffles).
  const totalPrizesToDraw = prizes.length > 1 ? prizes.length : 1
  const allPrizesDrawn = confirmedWinners.length >= totalPrizesToDraw

  // Only reflects the raffle's final winner once every prize has a confirmed ticket — between
  // rounds of a multi-prize draw this is null so the stage/controls reset to "ready to draw".
  const winnerTicket = useMemo(
    () => (allPrizesDrawn ? confirmedWinners[confirmedWinners.length - 1] ?? null : null),
    [allPrizesDrawn, confirmedWinners],
  )

  const eligibleTickets = useMemo(
    () => tickets.filter((ticket) => isValidTicket(ticket) && !ticket.isWinner),
    [tickets],
  )

  const filteredTickets = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    if (!normalizedQuery) return tickets
    return tickets.filter((ticket) => ticket.code.toLowerCase().includes(normalizedQuery))
  }, [searchQuery, tickets])

  const drawnWinners = useMemo<RaffleDrawingWinnerEntry[]>(() => {
    if (totalPrizesToDraw <= 1) return []
    return confirmedWinners.map((ticket, index) => {
      const prizeIndex = typeof ticket.prizeIndex === "number" ? ticket.prizeIndex : index
      return {
        ticket,
        label: ticket.prizeTitle || prizes[prizeIndex]?.title || `Prize ${prizeIndex + 1}`,
      }
    })
  }, [confirmedWinners, prizes, totalPrizesToDraw])

  const disabledReason = useMemo(() => {
    if (!isLiveModeLoaded) return "Loading live mode status."
    if (isLiveModeUpdating) return "Updating live mode status."
    if (!isLiveMode) return "Live mode is off. Enable live mode to start drawing."
    if (isLoadingTickets) return "Tickets are loading."
    if (isSpinning) return "Drawing in progress."
    if (allPrizesDrawn) return totalPrizesToDraw > 1 ? "All prize winners have been drawn." : "A winner already exists."
    if (tickets.length === 0) return "No tickets available."
    if (eligibleTickets.length === 0) return "No eligible tickets are available."
    return ""
  }, [allPrizesDrawn, eligibleTickets.length, isLiveMode, isLiveModeLoaded, isLiveModeUpdating, isLoadingTickets, isSpinning, tickets.length, totalPrizesToDraw])

  const handleToggleLiveMode = useCallback(async (nextLiveMode: boolean) => {
    if (isLiveModeUpdating) return

    const previousLiveMode = isLiveMode
    setIsLiveMode(nextLiveMode)
    setIsLiveModeUpdating(true)

    try {
      const raffleRef = doc(db, "raffles", raffleId)
      await updateDoc(raffleRef, {
        isLiveMode: nextLiveMode,
        ...(nextLiveMode
          ? {}
          : {
              isDrawingLive: false,
              drawingState: {
                status: "idle" as DrawingRuntimeStatus,
                actionId: createDrawingActionId(),
                startedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
            }),
      })
      PopupService.pushPopup(nextLiveMode ? "Live mode enabled." : "Live mode disabled.")
    } catch (error) {
      setIsLiveMode(previousLiveMode)
      PopupService.pushPopup(getErrorMessage(error, "Unable to update live mode."))
    } finally {
      setIsLiveModeUpdating(false)
    }
  }, [isLiveMode, isLiveModeUpdating, raffleId])

  const fetchTicketUserDetails = useCallback(async (ticket: RaffleTicketRecord) => {
    setDetailsLoading(true)
    setDetailsUser(null)

    try {
      if (!ticket.payerUID) {
        setDetailsUser(null)
        return null
      }

      const userDoc = doc(db, `users/${ticket.payerUID}`)
      const snap = await getDoc(userDoc)
      const userDetails = toUserDetails(snap.data() as UserRecord | undefined)
      setDetailsUser(userDetails)
      return userDetails
    } catch (error) {
      PopupService.pushPopup(getErrorMessage(error, "Unable to load ticket holder details."))
      setDetailsUser(null)
      return null
    } finally {
      setDetailsLoading(false)
    }
  }, [])

  const handleOpenDetails = useCallback(
    (ticket: RaffleDrawingTicket) => {
      const fullTicket = tickets.find((item) => item.id === ticket.id) || (ticket as RaffleTicketRecord)
      setDetailsTicket(fullTicket)
      setDetailsOpen(true)
      fetchTicketUserDetails(fullTicket)
    },
    [fetchTicketUserDetails, tickets],
  )

  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false)
  }, [])

  const handleSpin = useCallback(() => {
    if (disabledReason) {
      PopupService.pushPopup(disabledReason)
      return
    }

    setIsSpinning(true)
    setConfirmOpen(false)
    const randomIndex = Math.floor(Math.random() * eligibleTickets.length)
    const nextTicket = eligibleTickets[randomIndex]
    const spinDurationMs = prefersReducedMotion
      ? (drawingConfig.animation?.reducedMotionSpinMs ?? REDUCED_MOTION_SPIN_MS)
      : (drawingConfig.animation?.spinDurationMs ?? SPIN_DURATION_MS)

    updateDoc(doc(db, "raffles", raffleId), {
      drawingState: {
        status: "spinning" as DrawingRuntimeStatus,
        actionId: createDrawingActionId(),
        startedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        spinDurationMs,
      },
    }).catch(() => undefined)

    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current)
    }

    spinTimeoutRef.current = setTimeout(
      () => {
        setSelectedTicket(nextTicket)
        setDetailsTicket(nextTicket)
        setIsSpinning(false)
      },
      spinDurationMs,
    )
  }, [disabledReason, drawingConfig.animation, eligibleTickets, prefersReducedMotion, raffleId])

  const handleResetSelection = useCallback(() => {
    if (isSpinning || isSavingWinner) return
    setSelectedTicket(null)
    setConfirmOpen(false)
    if (!winnerTicket) {
      updateDoc(doc(db, "raffles", raffleId), {
        drawingState: {
          status: "idle" as DrawingRuntimeStatus,
          actionId: createDrawingActionId(),
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      }).catch(() => undefined)
    }
  }, [isSavingWinner, isSpinning, raffleId, winnerTicket])

  const handleOpenConfirm = useCallback(async () => {
    if (!selectedTicket || winnerTicket || isSpinning || isSavingWinner) return

    setDetailsTicket(selectedTicket)
    setConfirmOpen(true)
    await fetchTicketUserDetails(selectedTicket)
  }, [fetchTicketUserDetails, isSavingWinner, isSpinning, selectedTicket, winnerTicket])

  const handleCloseConfirm = useCallback(() => {
    if (!isSavingWinner) {
      setConfirmOpen(false)
    }
  }, [isSavingWinner])

  const handleConfirmWinner = useCallback(async () => {
    if (!user) {
      PopupService.setPopup("No logged in")
      return
    }

    if (!selectedTicket) {
      PopupService.setPopup("No ticket selected yet. Please spin the wheel")
      return
    }

    if (allPrizesDrawn) {
      PopupService.pushPopup(
        totalPrizesToDraw > 1
          ? "All prize winners have already been drawn."
          : "A winner is already marked for this raffle.",
      )
      return
    }

    if (!selectedTicket.payerUID) {
      PopupService.pushPopup("Selected ticket is missing a ticket holder.")
      return
    }

    setIsSavingWinner(true)

    try {
      const isMultiPrize = totalPrizesToDraw > 1
      const prizeIndex = confirmedWinners.length
      const prize = isMultiPrize ? prizes[prizeIndex] : undefined
      const winnerInfo = {
        isWinner: true,
        setWinnerDate: DateTime.now().toMillis(),
        setWinnerUserUID: user.uid,
        ...(isMultiPrize
          ? {
              prizeIndex,
              prizeId: prize?.id ?? null,
              prizeTitle: prize?.title ?? null,
            }
          : {}),
      }

      const raffleTicket = doc(db, `raffles/${raffleId}/tickets/${selectedTicket.id}`)
      await updateDoc(raffleTicket, winnerInfo)

      const userTicket = query(
        collection(db, `users/${selectedTicket.payerUID}/tickets`),
        where("code", "==", selectedTicket.code),
      )
      const userTicketSnapshot = await getDocs(userTicket)
      await Promise.all(userTicketSnapshot.docs.map(({ ref }) => updateDoc(ref, winnerInfo)))

      setTickets((currentTickets) =>
        currentTickets.map((ticket) =>
          ticket.id === selectedTicket.id
            ? {
                ...ticket,
                ...winnerInfo,
              }
            : ticket,
        ),
      )
      // Cleared unconditionally: for a multi-prize raffle this resets the stage/controls back
      // to "ready to draw" for the next prize; for the final (or only) prize it just clears the
      // now-redundant selection, since `winnerTicket` (derived from `confirmedWinners`) takes
      // over displaying the result.
      setSelectedTicket(null)
      await updateDoc(doc(db, "raffles", raffleId), {
        drawingState: {
          status: "winner" as DrawingRuntimeStatus,
          actionId: createDrawingActionId(),
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          winnerTicketId: selectedTicket.id,
          winnerTicketCode: selectedTicket.code,
          ...(isMultiPrize ? { prizeIndex, prizeTitle: prize?.title ?? null } : {}),
        },
      })
      setConfirmOpen(false)
      PopupService.pushPopup(
        isMultiPrize
          ? `${selectedTicket.code} marked as winner for ${prize?.title || `Prize ${prizeIndex + 1}`}.`
          : `${selectedTicket.code} marked as winner.`,
      )
    } catch (error) {
      PopupService.pushPopup(getErrorMessage(error, "Unable to mark winner."))
    } finally {
      setIsSavingWinner(false)
    }
  }, [allPrizesDrawn, confirmedWinners.length, prizes, raffleId, selectedTicket, totalPrizesToDraw, user])

  const handleConfigChange = useCallback((config: RaffleDrawingConfig) => {
    const normalizedConfig = normalizeDrawingConfig(config)
    setDrawingConfig(normalizedConfig)
    try {
      localStorage.setItem(DRAWING_CONFIG_STORAGE_KEY, JSON.stringify(normalizedConfig))
    } catch {
      // storage unavailable
    }
  }, [])

  const drawingViewProps = {
    tickets,
    filteredTickets,
    eligibleCount: eligibleTickets.length,
    selectedTicket,
    winnerTicket,
    drawnWinners,
    isLoading: isLoadingTickets,
    isLiveMode,
    isLiveModeUpdating,
    isSpinning,
    isSavingWinner,
    isFullscreen,
    searchQuery,
    disabledReason,
    confirmOpen,
    detailsOpen,
    detailsTicket,
    detailsUser,
    detailsLoading,
    prefersReducedMotion,
    config: drawingConfig,
    onConfigChange: handleConfigChange,
    onSearchChange: setSearchQuery,
    onSpin: handleSpin,
    onResetSelection: handleResetSelection,
    onOpenConfirm: handleOpenConfirm,
    onCloseConfirm: handleCloseConfirm,
    onConfirmWinner: handleConfirmWinner,
    onOpenDetails: handleOpenDetails,
    onCloseDetails: handleCloseDetails,
    onEnterFullscreen: () => setIsFullscreen(true),
    onExitFullscreen: () => setIsFullscreen(false),
    onToggleLiveMode: handleToggleLiveMode,
    viewerCount,
    showExpirationDate,
  }

  return (
    <RaffleDrawingView {...(drawingViewProps as any)} />
  )
}

export default Wheel
